import { Grid } from "@material-ui/core";
import { FieldSetBox } from "../fieldSetBox";
import { ReportField } from "./reportField";
import { observer } from "mobx-react";
import { formatCurrencyField, formatNumberField, formatPercentageField, getStringOrDefault, hasValue } from "../../utils/utils";
import { getLabelForHighestAndBestUseValue, getLabelForLocationValue, getLabelForNeighborhoodZoningValue } from "../../utils/constants";

const COLUMNS = 3;
const ROW_HEIGHT = 77;//4 rows = 308px, the height the box always had

interface SubjectNeighborhoodInfoBoxProps {
  internalValuationReview: any;
  neighborhoodHousingConditions: any;
}

interface NeighborhoodItem {
  label: string;
  value: string;
  trend?: {
    percentage: number;
    description: string;
  };
  hidden?: boolean;
}

export const SubjectNeighborhoodInfoBox = observer(({ internalValuationReview, neighborhoodHousingConditions }: SubjectNeighborhoodInfoBoxProps) => {
  //a trend without a value is not shown, the remaining items flow into the free spot
  const items: NeighborhoodItem[] = [
    {
      label: "Location",
      value: getStringOrDefault(getLabelForLocationValue(internalValuationReview.location)),
    },
    {
      label: "Property Price Trend: Monthly Median Price",
      value: formatCurrencyField(neighborhoodHousingConditions.propertyPriceTrendMonthlyMedianPrice),
      trend: {
        percentage: neighborhoodHousingConditions.yoyMedianPricePercentage,
        description: "vs Median Price 1 Year ago",
      },
      hidden: !hasValue(neighborhoodHousingConditions.propertyPriceTrendMonthlyMedianPrice),
    },
    {
      label: "REO/Short/Distressed Trend: Monthly Listings",
      value: formatNumberField(neighborhoodHousingConditions.reoShortDistressedTrendMonthlyListings),
      trend: {
        percentage: neighborhoodHousingConditions.yoyReoListingsPercentage,
        description: "vs REO Listings 1 Year Ago",
      },
      hidden: !hasValue(neighborhoodHousingConditions.reoShortDistressedTrendMonthlyListings),
    },
    {
      label: 'Number of "Comparable" Sales Past 12MO.',
      value: formatNumberField(neighborhoodHousingConditions.compsPast12Mo),
    },
    {
      label: '"Comparable" Sale Price Range',
      value: `${formatCurrencyField(neighborhoodHousingConditions.comparableSalePriceFrom)} - ${formatCurrencyField(neighborhoodHousingConditions.comparableSalePriceTo)}`,
    },
    {
      label: "Neighborhood Price Range",
      value: `${formatCurrencyField(neighborhoodHousingConditions.neighborhoodPriceFrom)} - ${formatCurrencyField(neighborhoodHousingConditions.neighborhoodPriceTo)}`,
    },
    {
      label: "DOM Trend",
      value: formatNumberField(neighborhoodHousingConditions.domTrend),
      trend: {
        percentage: neighborhoodHousingConditions.yoyDomPercentage,
        description: "vs DOM 1 Year Ago",
      },
      hidden: !hasValue(neighborhoodHousingConditions.domTrend),
    },
    {
      label: "Supply Trend Months of Supply",
      value: formatNumberField(neighborhoodHousingConditions.supplyTrendMonthsOfSupply),
      trend: {
        percentage: neighborhoodHousingConditions.yoyMonthsOfSupplyPercentage,
        description: "vs Months of Supply 1 Year Ago",
      },
      hidden: !hasValue(neighborhoodHousingConditions.supplyTrendMonthsOfSupply),
    },
    {
      label: "Demand Trend % of Sales Within 2 Weeks",
      value: formatPercentageField(neighborhoodHousingConditions.demandTrendPercentSalesWithin2Weeks),
      trend: {
        percentage: neighborhoodHousingConditions.yoyWithin2WeeksPercentage,
        description: "vs % of Sales within 2 Weeks 1 Year Ago",
      },
      hidden: !hasValue(neighborhoodHousingConditions.demandTrendPercentSalesWithin2Weeks),
    },
    {
      label: "Highest and Best Use",
      value: getStringOrDefault(getLabelForHighestAndBestUseValue(internalValuationReview.highestAndBestUse)),
    },
    {
      label: "Adverse Factors",
      value: internalValuationReview.hasAdverseFactors ? "Yes" : "No",
    },
    {
      label: "Neighborhood Zoning",
      value: getStringOrDefault(getLabelForNeighborhoodZoningValue(internalValuationReview.neighborhoodZoning)),
    },
  ].filter(item => !item.hidden);

  const rows = Math.ceil(items.length / COLUMNS);

  return (
    <FieldSetBox title="Subject Neighborhood Information & Housing Conditions">
      <Grid container style={{ height: `${rows * ROW_HEIGHT}px` }}>
        {items.map((item, index) => (
          <Grid item xs={4} key={item.label}>
            <ReportField
              label={item.label}
              value={item.value}
              trend={item.trend}
              removeRightBorder={index % COLUMNS === COLUMNS - 1 || index === items.length - 1}
            />
          </Grid>
        ))}
      </Grid>
    </FieldSetBox>
  );
});
